import { OTPInput, OTPInputContext } from 'input-otp'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

const InputOTP = ({
	className,
	containerClassName,
	...props
}: React.ComponentProps<typeof OTPInput>) => (
	<OTPInput
		data-slot="input-otp"
		containerClassName={cn(
			'flex items-center gap-2 has-disabled:opacity-50',
			containerClassName,
		)}
		className={cn('disabled:cursor-not-allowed', className)}
		{...props}
	/>
)

const InputOTPGroup = ({
	className,
	...props
}: React.ComponentProps<'div'>) => (
	<div
		data-slot="input-otp-group"
		className={cn('flex items-center', className)}
		{...props}
	/>
)

const InputOTPSlot = ({
	index,
	className,
	...props
}: React.ComponentProps<'div'> & {
	index: number
}) => {
	const inputOTPContext = React.useContext(OTPInputContext)
	const slot = inputOTPContext.slots[index]
	if (!slot) throw new Error('Invalid slot index')
	const { char, hasFakeCaret, isActive } = slot

	return (
		<div
			data-slot="input-otp-slot"
			className={cn(
				// size-11 below sm keeps each slot at the 44px touch-target minimum
				// (WCAG 2.5.5); aria-invalid on the field alone was silent for
				// sighted users, so the group carries a data-invalid tint.
				'border-input relative flex size-11 items-center justify-center border-y border-r text-base transition-all first:rounded-l-md first:border-l last:rounded-r-md sm:size-10 md:text-sm',
				'group-[.is-invalid]:border-destructive group-[.is-invalid]:text-destructive',
				isActive && 'ring-ring ring-offset-background z-10 ring-2',
				className,
			)}
			{...props}
		>
			{char}
			{hasFakeCaret && (
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
					<div className="animate-caret-blink bg-foreground h-4 w-px duration-1000" />
				</div>
			)}
		</div>
	)
}

/**
 * Was an empty `flex-1` spacer: it pushed the two groups 86px apart with
 * nothing between them, so the field read as three cells, a hole, three cells.
 * A hairline in the system's rule colour is the mark that was missing, and
 * dropping `flex-1` lets the groups sit together as one control instead of
 * stretching to the container's full width.
 */
const InputOTPSeparator = (props: React.ComponentProps<'div'>) => (
	<div
		data-slot="input-otp-separator"
		aria-hidden
		className="flex items-center px-2"
		{...props}
	>
		<span className="bg-border h-px w-3" />
	</div>
)

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator }
